import { FormatOption } from '@/modules/base/components/inputs/html-input/_helpers/getExtensions'

/**
 * Checks if any basic formatting options are included in the formats array
 */
export const hasBasicFormats = (formats: FormatOption[]): boolean => {
  const basicFormats: FormatOption[] = [
    'bold', 'italic', 'underline', 'fontSize', 'fontColor', 'fontBackgroundColor',
  ]
  return basicFormats.some(format => formats.includes(format))
}

/**
 * Checks if undo or redo formats are included in the formats array
 */
export const hasUndoRedoFormats = (formats: FormatOption[]): boolean => {
  return formats.includes('undo') || formats.includes('redo')
}

/**
 * Checks if any list formatting options are included in the formats array
 */
export const hasListFormats = (formats: FormatOption[]): boolean => {
  return formats.includes('bulletedList')
    || formats.includes('numberedList')
    || formats.includes('NumberedListWithTypes')
}

/**
 * Node types that count as content even without text (e.g. pageBreak, placeholder, mjmlButton)
 */
const CONTENT_NODE_TYPES = ['pageBreak', 'placeholder', 'mjmlButton', 'image', 'horizontalRule']

// U+00A0 (not the HTML entity) so it can be assigned to Text.data directly; the browser's own
// HTML serializer escapes it to &nbsp; when we read .innerHTML back out.
const NBSP_CHAR = ' '

const preserveSpacesInText = (text: string): string => {
  return text
    .replace(/^ +/g, (spaces) => NBSP_CHAR.repeat(spaces.length))
    .replace(/ +$/g, (spaces) => NBSP_CHAR.repeat(spaces.length))
    .replace(/ {2,}/g, (spaces) => ' ' + NBSP_CHAR.repeat(spaces.length - 1))
}

/**
 * Replaces leading/trailing/repeated spaces in text nodes with non-breaking spaces so the
 * browser doesn't collapse them. Walks the parsed DOM (rather than regexing the raw HTML string)
 * so it only ever touches genuine text content, never attribute values (e.g. an `alt` or `title`
 * containing a literal ">") or tag markup.
 */
export const preserveWhitespaceInHtml = (html: string): string => {
  if (!html) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)

  let textNode = walker.nextNode()
  while (textNode) {
    textNode.textContent = preserveSpacesInText(textNode.textContent ?? '')
    textNode = walker.nextNode()
  }

  return doc.body.innerHTML
}

/**
 * Checks if the editor content is truly empty (no visible text and no special content nodes)
 */
export const isEditorEmpty = (editor: import('@tiptap/react').Editor | null): boolean => {
  if (!editor) return true

  // Check for text content first
  const textContent = editor.getText()?.trim() || ''
  if (textContent.length > 0) return false

  // Check for special content nodes (like pageBreak) that don't have text
  const json = editor.getJSON()
  if (!json.content) return true

  const hasContentNodes = json.content.some((node: { type?: string }) =>
    node.type && CONTENT_NODE_TYPES.includes(node.type),
  )

  return !hasContentNodes
}
